import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { BookOpen, Smartphone, Network, Shield, Database } from 'lucide-react';
interface SlideMoatProps {
  onContinue: () => void;
}
export function SlideMoat({
  onContinue
}: SlideMoatProps) {
  const [stage, setStage] = useState(0);
  const moats = [{
    title: 'Medical Storytelling',
    desc: 'Codified pharma expertise that generalist AI cannot replicate.',
    icon: <BookOpen className="w-6 h-6 text-teal-400" data-id="element-653" data-mp="3hc1ck732md4s" />,
    tint: 'teal'
  }, {
    title: 'Interactive Platform',
    desc: 'Short-format engagement driving measurable outcomes.',
    icon: <Smartphone className="w-6 h-6 text-purple-400" data-id="element-654" data-mp="38b3a3t8a6dob" />,
    tint: 'violet'
  }, {
    title: 'Pharma Distribution',
    desc: 'Deep relationships and master agreements in a regulated vertical.',
    icon: <Network className="w-6 h-6 text-rose-400" data-id="element-655" data-mp="16j6ykjm453tj" />,
    tint: 'rose'
  }, {
    title: 'Behavioral Dataset',
    desc: 'Privacy-safe data that compounds performance with every campaign.',
    icon: <Database className="w-6 h-6 text-sky-400" data-id="element-656" data-mp="2dzurvh7bmz9j" />,
    tint: 'blue'
  }];
  const tintStyles = {
    rose: {
      bg: 'bg-rose-500/5',
      border: 'border-rose-400/30',
      iconBg: 'bg-rose-500/15',
      iconColor: 'text-rose-400'
    },
    teal: {
      bg: 'bg-teal-400/5',
      border: 'border-teal-400/30',
      iconBg: 'bg-teal-400/15',
      iconColor: 'text-teal-400'
    },
    blue: {
      bg: 'bg-blue-500/5',
      border: 'border-blue-400/30',
      iconBg: 'bg-blue-500/15',
      iconColor: 'text-blue-400'
    },
    violet: {
      bg: 'bg-violet-500/5',
      border: 'border-violet-400/30',
      iconBg: 'bg-violet-500/15',
      iconColor: 'text-violet-400'
    }
  } as const;
  useEffect(() => {
    const t1 = setTimeout(() => setStage(1), 300);
    const timers = moats.map((_, idx) => setTimeout(() => setStage(2 + idx), 800 + idx * 250));
    const t2 = setTimeout(() => setStage(2 + moats.length), 800 + moats.length * 250 + 200);
    const t3 = setTimeout(() => setStage(3 + moats.length), 800 + moats.length * 250 + 800);
    return () => {
      clearTimeout(t1);
      timers.forEach(clearTimeout);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, []);
  return <div className="h-full w-full flex flex-col px-8 py-10 md:px-20 md:py-12 max-w-7xl mx-auto" data-id="element-657" data-mp="37b7h38oqmrxy">
      {/* Heading */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="text-center space-y-4" data-id="element-658" data-mp="32jxukgyhk4qa">
        <h2 className="text-4xl md:text-5xl font-serif text-white" data-id="element-659" data-mp="2smtemyv8h5hx">The Moat</h2>
        <p className="text-xl text-white/60" data-id="element-660" data-mp="2nmyqh32ueu0k">
          Four compounding assets that defend the model.
        </p>
      </motion.div>

      <div className="flex-1 flex flex-col justify-center mt-10" data-id="element-661" data-mp="2jmob4zt5ur91">
        {/* 4 pillar cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 relative z-10" data-id="element-662" data-mp="z2j5vpknr31o">
          {moats.map((p, idx) => {
          const style = tintStyles[p.tint as keyof typeof tintStyles];
          return <motion.div key={p.title} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: stage >= 2 + idx ? 1 : 0,
            y: stage >= 2 + idx ? 0 : 20
          }} transition={{
            duration: 0.5
          }} className={`${style.bg} border ${style.border} rounded-2xl p-5 flex flex-col`} data-id="element-663" data-mp="185ka1kpyqk2p" data-mpi={idx}>
                <div className={`w-10 h-10 rounded-xl ${style.iconBg} flex items-center justify-center ${style.iconColor} mb-5`} data-id="element-664" data-mp="u59hmd908xff">
                  {p.icon}
                </div>
                <h3 className="text-base md:text-lg font-bold text-white leading-tight" data-id="element-665" data-mp="1mcatmfzxxeg">
                  {p.title}
                </h3>
                <p className="text-white/55 text-sm mt-3 leading-snug" data-id="element-666" data-mp="1zbr5hhj3h4wz">
                  {p.desc}
                </p>
              </motion.div>;
        })}
        </div>

        {/* Lock + Unbreakable Flywheel */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: stage >= 5 ? 1 : 0,
        scale: stage >= 5 ? 1 : 0.9
      }} className="mt-12 bg-navy-900/50 border border-navy-700 rounded-2xl p-8 text-center relative overflow-hidden" data-id="element-667" data-mp="28c76mkcd7zyo">
          <div className="absolute inset-0 bg-gradient-to-r from-teal-400/5 via-purple-500/5 to-rose-400/5" data-id="element-668" data-mp="2vzxh4oefoxkr" />
          <div className="relative z-10" data-id="element-669" data-mp="2r146d3qj7koa">
            <div className="w-16 h-16 mx-auto bg-white/5 rounded-full flex items-center justify-center mb-4 border border-white/10" data-id="element-670" data-mp="11p1ysudg644s">
              <Shield className="w-8 h-8 text-white" data-id="element-671" data-mp="wqqokij8prm1" />
            </div>
            <h3 className="text-2xl font-serif text-white mb-2" data-id="element-672" data-mp="38dasemhcme41">
              The Unbreakable Flywheel
            </h3>
            <p className="text-white/70 max-w-2xl mx-auto" data-id="element-673" data-mp="3daknahnibhz9">
              Together, these assets create a barrier to entry that no broad,
              generalist player can match inside this regulated vertical.
            </p>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: stage >= 6 ? 1 : 0
      }} className="mt-12 flex justify-center" data-id="element-674" data-mp="2pli5n9svhp4r"></motion.div>
      </div>
    </div>;
}